import { Clock, Plus, X, FloppyDisk, PencilSimple, Check } from '@phosphor-icons/react';

// Same safe line-break renderer used elsewhere on the page.
function HtmlContent({ html, className = '' }) {
  if (!html) return <span>-</span>;
  const lines = String(html).split(/\r?\n/);
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={`ln-${i}-${line.slice(0, 8)}`}>{line}{i < lines.length - 1 && <br />}</span>
      ))}
    </span>
  );
}

// ── Sub-component: form to register a new deal state ──
function AddStateForm({ stateForm, setStateForm, events, results, saving, saveMsg, onSave, onCancel }) {
  return (
    <div data-testid="add-state-form" className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4 mb-4 space-y-3">
      <h4 className="text-xs font-semibold text-[#111827] uppercase tracking-wider">Registar Novo Estado</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Evento">
          <select data-testid="state-event" value={stateForm.ID_Evento} onChange={e => setStateForm({ ...stateForm, ID_Evento: e.target.value })} className={SELECT_CLS}>
            <option value="">Selecionar...</option>
            {events.map(ev => <option key={ev.ID_EstadoNegocio} value={ev.ID_EstadoNegocio}>{ev.Descricao}</option>)}
          </select>
        </Field>
        <Field label="Resultado">
          <select data-testid="state-result" value={stateForm.ID_Resultado} onChange={e => setStateForm({ ...stateForm, ID_Resultado: e.target.value })} className={SELECT_CLS}>
            <option value="">Selecionar...</option>
            {results.map(r => <option key={r.Id} value={r.Id}>{r.Descricao}</option>)}
          </select>
        </Field>
        <Field label="Nome Pessoa">
          <input data-testid="state-name" value={stateForm.NomePessoa} onChange={e => setStateForm({ ...stateForm, NomePessoa: e.target.value })} className={SELECT_CLS} placeholder="Nome..." />
        </Field>
        <Field label="Contacto Pessoa">
          <input data-testid="state-contact" value={stateForm.ContactoPessoa} onChange={e => setStateForm({ ...stateForm, ContactoPessoa: e.target.value })} className={SELECT_CLS} placeholder="Contacto..." />
        </Field>
        <Field label="Data Início">
          <input data-testid="state-date-start" type="datetime-local" value={stateForm.DataInicio} onChange={e => setStateForm({ ...stateForm, DataInicio: e.target.value })} className={SELECT_CLS} />
        </Field>
        <Field label="Data Fim">
          <input data-testid="state-date-end" type="datetime-local" value={stateForm.DataFim} onChange={e => setStateForm({ ...stateForm, DataFim: e.target.value })} className={SELECT_CLS} />
        </Field>
        <Field label="Valor Previsto">
          <input data-testid="state-value" type="number" step="0.01" value={stateForm.ValorPrevisto} onChange={e => setStateForm({ ...stateForm, ValorPrevisto: e.target.value })} className={SELECT_CLS} />
        </Field>
        <Field label="ID Encomenda (opcional)">
          <input data-testid="state-order" value={stateForm.ID_Encomenda} onChange={e => setStateForm({ ...stateForm, ID_Encomenda: e.target.value })} className={SELECT_CLS} placeholder="Ex: 69730" />
        </Field>
      </div>
      <Field label="Observações">
        <textarea data-testid="state-obs" value={stateForm.Observacoes} onChange={e => setStateForm({ ...stateForm, Observacoes: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded resize-none focus:ring-2 focus:ring-[#0F1829] outline-none" rows={2} placeholder="Observações..." />
      </Field>
      <div className="flex items-center gap-2">
        <button data-testid="save-state-btn" onClick={onSave} disabled={saving}
          className="flex items-center gap-1 px-4 py-1.5 text-xs font-medium bg-[#0F1829] text-white rounded hover:bg-[#001f7a] disabled:opacity-50">
          <FloppyDisk size={14} /> {saving ? 'A guardar...' : 'Guardar'}
        </button>
        <button onClick={onCancel} className="px-4 py-1.5 text-xs font-medium border border-[#E5E7EB] text-[#374151] rounded hover:bg-white">
          <X size={14} className="inline mr-1" />Cancelar
        </button>
        {saveMsg && <p className="text-xs text-red-600 ml-2">{saveMsg}</p>}
      </div>
    </div>
  );
}

// ── Sub-component: inline form to edit the last state's dates/value/observation ──
function EditLastStateForm({ editingState, setEditingState, editingLowerBound, editSaveMsg, savingDate, onSave, onCancel }) {
  return (
    <div className="mt-2 space-y-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded p-3" data-testid="edit-state-date-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Data Início *</label>
          <input
            type="datetime-local"
            value={editingState.DataInicio}
            min={editingLowerBound || undefined}
            onChange={(ev) => setEditingState((s) => ({ ...s, DataInicio: ev.target.value }))}
            data-testid="edit-state-date-input"
            className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Data Fim</label>
          <input
            type="datetime-local"
            value={editingState.DataFim}
            min={editingState.DataInicio || editingLowerBound || undefined}
            onChange={(ev) => setEditingState((s) => ({ ...s, DataFim: ev.target.value }))}
            data-testid="edit-state-datafim-input"
            className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none"
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Valor Previsto (€)</label>
        <input
          type="number"
          step="0.01"
          value={editingState.ValorPrevisto}
          onChange={(ev) => setEditingState((s) => ({ ...s, ValorPrevisto: ev.target.value }))}
          data-testid="edit-state-valor-input"
          className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none"
        />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Observações</label>
        <textarea
          rows={3}
          value={editingState.Observacao}
          onChange={(ev) => setEditingState((s) => ({ ...s, Observacao: ev.target.value }))}
          data-testid="edit-state-obs-input"
          className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none resize-y"
        />
      </div>
      {editingLowerBound && (
        <p className="text-[10px] text-[#6B7280]">Data Início não pode ser anterior a {editingLowerBound.replace('T', ' ')}.</p>
      )}
      {editSaveMsg && <p className="text-[11px] text-red-600">{editSaveMsg}</p>}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={onSave}
          disabled={savingDate}
          data-testid="edit-state-date-save"
          className="flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-wider font-medium bg-[#0F1829] text-white rounded hover:bg-[#001f7a] disabled:opacity-50"
        >
          <Check size={11} /> {savingDate ? "A guardar..." : "Guardar"}
        </button>
        <button
          onClick={onCancel}
          data-testid="edit-state-date-cancel"
          className="flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-wider border border-[#E5E7EB] text-[#374151] rounded hover:bg-white"
        >
          <X size={11} /> Cancelar
        </button>
      </div>
    </div>
  );
}

// ── Shared form helpers ──
const SELECT_CLS = "w-full px-3 py-1.5 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">{label}</label>
      {children}
    </div>
  );
}

/**
 * Deal timeline showing chronological states with an "Add State" button and
 * inline editing of the *last* state (only the last one is editable).
 */
export default function DealStateTimeline({
  states,
  showAddState, stateForm, setStateForm, events, results, saving, saveMsg, onOpenAddState, onSaveAddState, onCancelAddState,
  editingStateId, editingState, setEditingState, editingLowerBound, editSaveMsg, savingDate, onStartEditLast, onSaveEditLast, onCancelEditLast,
}) {
  const list = states || [];
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#E5E7EB]">
        <h3 className="text-sm font-semibold text-[#111827] flex items-center gap-2">
          <Clock size={16} className="text-[#0F1829]" /> Timeline de Estados
        </h3>
        <button data-testid="add-state-btn" onClick={onOpenAddState}
          className="flex items-center gap-1 text-xs font-medium text-[#0F1829] hover:underline">
          <Plus size={12} /> Novo Estado
        </button>
      </div>

      {showAddState && (
        <AddStateForm
          stateForm={stateForm}
          setStateForm={setStateForm}
          events={events}
          results={results}
          saving={saving}
          saveMsg={saveMsg}
          onSave={onSaveAddState}
          onCancel={onCancelAddState}
        />
      )}

      {list.length > 0 ? (
        <div className="space-y-0">
          {list.map((e, i) => {
            const isLast = i === list.length - 1;
            const isEditing = editingStateId === e.ID_NegocioEstadoNegocio;
            return (
              <div key={e.ID_NegocioEstadoNegocio || `ne-${i}`} className="flex gap-4 relative">
                <div className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isLast ? 'bg-[#0F1829]' : 'bg-[#D1D5DB]'}`} />
                  {i < list.length - 1 && <div className="w-px flex-1 bg-[#E5E7EB]" />}
                </div>
                <div className="pb-4 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[#111827]">{e.EstadoNegocio}</span>
                    {e.Resultado && (
                      <span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-200">
                        {e.Resultado}
                      </span>
                    )}
                    {isLast && !isEditing && (
                      <button
                        onClick={() => onStartEditLast(e)}
                        data-testid={`edit-state-date-${e.ID_NegocioEstadoNegocio}`}
                        title="Editar último estado"
                        className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#6B7280] hover:text-[#0F1829] px-2 py-0.5 rounded hover:bg-[#F9FAFB] transition"
                      >
                        <PencilSimple size={11} /> Editar
                      </button>
                    )}
                  </div>
                  {isEditing ? (
                    <EditLastStateForm
                      editingState={editingState}
                      setEditingState={setEditingState}
                      editingLowerBound={editingLowerBound}
                      editSaveMsg={editSaveMsg}
                      savingDate={savingDate}
                      onSave={() => onSaveEditLast(e.ID_NegocioEstadoNegocio)}
                      onCancel={onCancelEditLast}
                    />
                  ) : (
                    <p className="text-xs text-[#6B7280] mt-0.5">{e.DataExecucao || e.DataRegisto || '-'}</p>
                  )}
                  {e.Observacao && (
                    <div className="text-xs text-[#6B7280] mt-1">
                      <HtmlContent html={e.Observacao} />
                    </div>
                  )}

                  {/* Tentativas de contacto feitas enquanto a lead esteve
                      neste estado (sem transição): Não atendeu, WhatsApp,
                      email, etc. Vêm ordenadas cronologicamente do backend. */}
                  {Array.isArray(e.Tentativas) && e.Tentativas.length > 0 && (
                    <div
                      className="mt-2 space-y-1 border-l-2 border-[#F0EBDF] pl-2.5"
                      data-testid={`state-tentativas-${e.ID_NegocioEstadoNegocio}`}
                    >
                      {e.Tentativas.map((t) => (
                        <div
                          key={t.ID_Tentativa}
                          data-testid={`tentativa-${t.ID_Tentativa}`}
                          className="flex items-center gap-1.5 text-[11px] text-[#6B7280]"
                        >
                          <span
                            className={
                              t.Tipo === "call_no_answer"
                                ? "inline-block w-1.5 h-1.5 rounded-full bg-red-500"
                                : t.Tipo === "call_answered"
                                ? "inline-block w-1.5 h-1.5 rounded-full bg-green-500"
                                : "inline-block w-1.5 h-1.5 rounded-full bg-blue-400"
                            }
                          />
                          <span className="font-medium text-[#111827]">{t.TipoLabel}</span>
                          <span className="tabular-nums">· {t.DataRegisto}</span>
                          {t.Observacao && t.Observacao !== t.TipoLabel && (
                            <span className="italic truncate">— {t.Observacao}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-[#6B7280]">Sem estados registados</p>
      )}
    </div>
  );
}
